// ============================================================================
// SEO BUILDER — pure module (no imports) shared by the browser (Layout.jsx)
// and the post-build prerender script (scripts/seo-prerender.js).
// Titles/descriptions live in i18n/translations.js -> seo; business data in site.js.
// ============================================================================

export const HREFLANG = { fr: "fr-BE", nl: "nl-BE", tr: "tr-BE", en: "en-BE" };
export const OG_LOCALE = { fr: "fr_BE", nl: "nl_BE", tr: "tr_TR", en: "en_GB" };

export const pageUrl = (siteUrl, pageSlugs, lang, pageKey) => {
  const slug = pageSlugs[pageKey][lang];
  return slug ? `${siteUrl}/${lang}/${slug}` : `${siteUrl}/${lang}`;
};

const serviceCatalog = (t) => ({
  "@type": "OfferCatalog",
  name: `${t.nav.mechanical} & ${t.nav.tires}`,
  itemListElement: [
    ...t.mechanicalPage.blocks.map((b) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: b.name, description: b.desc } })),
    ...t.tiresPage.cards.map((c) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: c.name, description: c.desc } })),
    ...t.tiresPage.choose.map((c) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: c.name, description: c.desc } })),
  ],
});

export const buildHead = ({ siteConfig, translations, pageSlugs, langs, defaultLang, lang, pageKey }) => {
  const t = translations[lang];
  const meta = t.seo[pageKey];
  const siteUrl = siteConfig.siteUrl;
  const canonical = pageUrl(siteUrl, pageSlugs, lang, pageKey);
  const title = `${meta.title} | ${siteConfig.companyName}`;
  const ogImage = `${siteUrl}/og.jpg`;
  const businessId = `${siteUrl}/#business`;

  const alternates = [
    ...langs.map((l) => ({ hreflang: HREFLANG[l], href: pageUrl(siteUrl, pageSlugs, l, pageKey) })),
    { hreflang: "x-default", href: pageUrl(siteUrl, pageSlugs, defaultLang, pageKey) },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AutoRepair",
        "@id": businessId,
        name: siteConfig.companyName,
        url: `${siteUrl}/${defaultLang}`,
        image: ogImage,
        logo: siteConfig.logo,
        description: t.seo.home.description,
        telephone: siteConfig.phoneHref.replace("tel:", ""),
        email: siteConfig.email,
        priceRange: "€€",
        address: {
          "@type": "PostalAddress",
          streetAddress: siteConfig.address.street,
          postalCode: siteConfig.address.postalCode,
          addressLocality: siteConfig.address.locality,
          addressRegion: "Bruxelles-Capitale",
          addressCountry: "BE",
        },
        areaServed: [{ "@type": "City", name: "Bruxelles" }, { "@type": "Place", name: "Haren" }],
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
            opens: "09:00",
            closes: "18:00",
          },
        ],
        hasOfferCatalog: serviceCatalog(t),
      },
      {
        "@type": "WebPage",
        "@id": `${canonical}#webpage`,
        url: canonical,
        name: title,
        description: meta.description,
        inLanguage: HREFLANG[lang],
        about: { "@id": businessId },
        primaryImageOfPage: ogImage,
      },
    ],
  };

  return {
    lang,
    title,
    description: meta.description,
    canonical,
    alternates,
    og: {
      type: "website",
      siteName: siteConfig.companyName,
      locale: OG_LOCALE[lang],
      title,
      description: meta.description,
      url: canonical,
      image: ogImage,
    },
    jsonLd,
  };
};
